import { apiClient } from './apiClient';
import { useAuthStore } from '@/stores/authStore';

export interface Book {
  id: number;
  title: string;
  author?: string;
  isbn?: string;
  publisher?: string;
  publication_year?: number;
  category_id?: number;
  category?: {
    id: number;
    name: string;
  };
  quantity: number;
  available_quantity: number;
  effective_available_quantity?: number;
  image_url?: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export interface LibraryStats {
  total_books: number;
  total_ebooks: number;
  active_borrows: number;
  total_categories: number;
  available_books: number;
  borrowed_books: number;
}

export interface BorrowStatus {
  id: number;
  book_id: number;
  status: 'Pending' | 'Approved' | 'Rejected' | 'Returned' | 'Returned_Pending_Confirmation';
  borrow_date: string;
  due_date?: string;
  return_date?: string;
  user_id: number;
  book: {
    id: number;
    title: string;
    author?: string;
    image_url?: string;
    category?: { id: number; name: string } | null;
  };
  rejection_reason?: string;
  notes?: string;
  borrower?: {
    id: number;
    full_name?: string;
    email: string;
  };
  issuer?: {
    id: number;
    full_name?: string;
    email: string;
  };
  return_receiver?: {
    id: number;
    full_name?: string;
    email: string;
  };
}

export interface Review {
  id: number;
  user_id: number;
  book_id: number;
  rating: number;
  review_text: string;
  created_at: string;
  user: {
    id: number;
    full_name: string;
    profile_picture_url?: string;
    username: string;
  };
}

export interface ReviewCreate {
  book_id: number;
  rating: number;
  review_text: string;
}

export interface Category {
  id: number;
  name: string;
  description?: string;
}

export interface BookCreateUpdate {
  title: string;
  author: string;
  isbn?: string;
  publisher?: string;
  publication_year?: number;
  category_id?: number;
  description?: string;
  quantity: number;
  available_quantity: number;
}

export const libraryService = {
  async getPhysicalBooks(skip: number = 0, limit: number = 100, category_id?: number): Promise<Book[]> {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });
    
    if (category_id) {
      params.append('category_id', category_id.toString());
    }
    
    return apiClient.get(`/library/physical-books/?${params.toString()}`);
  },

  async getPhysicalBook(bookId: number): Promise<Book> {
    return apiClient.get(`/library/physical-books/${bookId}`);
  },

  async getLibraryStats(): Promise<LibraryStats> {
    return apiClient.get('/library/stats');
  },

  async getBookBorrowStatus(bookId: number): Promise<BorrowStatus | null> {
    try {
      const borrows = await apiClient.get('/library/my-borrows/');
      const activeBorrow = borrows.find((borrow: BorrowStatus) => 
        borrow.book_id === bookId && 
        ['Pending', 'Approved'].includes(borrow.status) &&
        !borrow.return_date
      );
      return activeBorrow || null;
    } catch (error) {
      console.error('Error fetching borrow status:', error);
      return null;
    }
  },

  async borrowBook(bookId: number, notes?: string): Promise<void> {
    const { user } = useAuthStore.getState();
    if (!user?.id) {
      throw new Error('User not authenticated');
    }
    
    const borrowData: any = {
      book_id: bookId,
      user_id: user.id,
    };
    if (notes) {
      borrowData.notes = notes;
    }
    await apiClient.post('/library/borrow/', borrowData);
  },

  async returnBook(borrowLogId: number, notes?: string): Promise<void> {
    const returnData: any = {
      return_date: new Date().toISOString(),
    };
    if (notes) {
      returnData.notes = notes;
    }
    await apiClient.post(`/library/return/${borrowLogId}`, returnData);
  },

  async checkUserCanBorrow(): Promise<{ can_borrow: boolean; reason?: string }> {
    try {
      return await apiClient.get('/library/can-borrow/');
    } catch (error) {
      return { can_borrow: false, reason: 'Unable to check eligibility' };
    }
  },

  async getBookReviews(bookId: number): Promise<Review[]> {
    return apiClient.get(`/library/reviews?book_id=${bookId}`);
  },

  async submitReview(reviewData: ReviewCreate): Promise<void> {
    await apiClient.post('/library/reviews', reviewData);
  },

  async getMyBorrows(): Promise<BorrowStatus[]> {
    return apiClient.get('/library/my-borrows/');
  },

  async getCategories(): Promise<Category[]> {
    return apiClient.get('/library/categories/');
  },

  async createPhysicalBook(bookData: BookCreateUpdate, imageFile: File | null): Promise<Book> {
    const formData = new FormData();
    formData.append('title', bookData.title);
    formData.append('author', bookData.author);
    formData.append('quantity', bookData.quantity.toString());
    formData.append('available_quantity', bookData.available_quantity.toString());
    
    if (bookData.isbn) formData.append('isbn', bookData.isbn);
    if (bookData.publisher) formData.append('publisher', bookData.publisher);
    if (bookData.publication_year) formData.append('publication_year', bookData.publication_year.toString());
    if (bookData.category_id) formData.append('category_id', bookData.category_id.toString());
    if (bookData.description) formData.append('description', bookData.description);
    if (imageFile) formData.append('image_file', imageFile);

    return apiClient.post('/library/physical-books/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  async updatePhysicalBook(bookId: number, bookData: BookCreateUpdate, imageFile: File | null): Promise<Book> {
    const formData = new FormData();
    formData.append('title', bookData.title);
    formData.append('author', bookData.author);
    formData.append('quantity', bookData.quantity.toString());
    formData.append('available_quantity', bookData.available_quantity.toString());
    
    if (bookData.isbn) formData.append('isbn', bookData.isbn);
    if (bookData.publisher) formData.append('publisher', bookData.publisher);
    if (bookData.publication_year) formData.append('publication_year', bookData.publication_year.toString());
    if (bookData.category_id) formData.append('category_id', bookData.category_id.toString());
    if (bookData.description) formData.append('description', bookData.description);
    if (imageFile) formData.append('image_file', imageFile);

    return apiClient.put(`/library/physical-books/${bookId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  async deletePhysicalBook(bookId: number): Promise<void> {
    await apiClient.delete(`/library/physical-books/${bookId}`);
  },

  // Category management
  async createCategory(categoryData: { name: string; description?: string }): Promise<Category> {
    return apiClient.post('/library/categories/', categoryData);
  },

  async updateCategory(categoryId: number, categoryData: { name: string; description?: string }): Promise<Category> {
    return apiClient.put(`/library/categories/${categoryId}`, categoryData);
  },

  async deleteCategory(categoryId: number): Promise<void> {
    await apiClient.delete(`/library/categories/${categoryId}`);
  },

  // Borrow request management
  async getBorrowRequests(): Promise<BorrowStatus[]> {
    return apiClient.get('/library/borrow-logs/');
  },

  async approveBorrowRequest(requestId: number, notes?: string): Promise<void> {
    const body: any = {};
    if (notes) {
      body.notes = notes;
    }
    await apiClient.post(`/library/borrow-requests/${requestId}/approve`, body);
  },

  async rejectBorrowRequest(requestId: number, rejectionReason: string, notes?: string): Promise<void> {
    const body: any = {
      rejection_reason: rejectionReason,
    };
    if (notes) {
      body.notes = notes;
    }
    await apiClient.post(`/library/borrow-requests/${requestId}/reject`, body);
  },

  async confirmReturn(requestId: number, confirmationNotes?: string): Promise<void> {
    const body: any = {};
    if (confirmationNotes) {
      body.confirmation_notes = confirmationNotes;
    }
    await apiClient.post(`/library/return/${requestId}/confirm`, body);
  },

  // Library records
  async getAllLibraryRecords(): Promise<any[]> {
    return apiClient.get('/library/borrows/all');
  },

  // Library settings
  async getLibrarySettings(): Promise<any> {
    return apiClient.get('/library/settings/');
  },

  async updateLibrarySettings(settings: any): Promise<any> {
    return apiClient.put('/library/settings/', settings);
  },

  // Penalty management
  async calculatePenalty(borrowLogId: number): Promise<any> {
    return apiClient.get(`/library/penalties/calculate/${borrowLogId}`);
  },

  async adjustPenalty(adjustmentData: any): Promise<any> {
    return apiClient.post('/library/penalties/adjust', adjustmentData);
  },

  async getPenaltyAdjustments(borrowLogId: number): Promise<any[]> {
    return apiClient.get(`/library/penalties/adjustments/${borrowLogId}`);
  },

  // Due date extensions
  async getDueDateExtensions(borrowLogId: number): Promise<any[]> {
    return apiClient.get(`/library/extensions/${borrowLogId}`);
  },

  async extendDueDate(extensionData: any): Promise<any> {
    return apiClient.post('/library/extensions/', extensionData);
  },

  // Library reports
  async getOverdueBooks(): Promise<any[]> {
    return apiClient.get('/library/reports/overdue');
  },

  async getBooksDueSoon(days: number = 3): Promise<any[]> {
    return apiClient.get(`/library/reports/due-soon?days=${days}`);
  },

  async getPenaltySummary(): Promise<any> {
    return apiClient.get('/library/reports/penalty-summary');
  },
};
